/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-param-reassign */
import _ from 'lodash';
import React, {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import arrayMove from 'array-move';
import { v4 } from 'uuid';

import {
  Field,
  FieldArray,
  Form as FormikForm,
} from 'formik';

import {
  Accordion,
  Form,
  Icon,
} from 'semantic-ui-react';

import {
  ErrorHandler,
  TextAreaFormField,
  InputFormField,
  Toggle,
  ContainerToggle,
  TextToggle,
} from '@playbookux/playbook-web-core';

import CheckboxAccordion from './checkbox-accordion';

import SelectList from '../../../core/components/select-list';
import Categorizing from '../../../core/components/categorizing-select-list';
import SelectFormField from '../../../core/components/select-form-field';

import { SortableList } from '../sortable-component';

import api from '../../../core/services/api';

import {
  companySize,
  ethnicity,
  gender,
  householdIncome,
  industry,
  screenerSingle,
  screenerMultiple,
  seniority,
  states,
  workStatus,
  countries,
  remainingCountriesList,
  languages,
} from './options';

import {
  AddQuestion,
  AddScreenerQuestion,
  AnswersContainer,
  Container,
  ContainerAccordion,
  CustomAccordion,
  FloatRight,
  FormContainer,
  HeaderQuestions,
  HeaderSegmentContainer,
  IconCircle,
  InputAnswerContainer,
  QualifyContainer,
  QuestionField,
  QuestionSegment,
  ScreenerQuestionContainer,
  SectionContainer,
  SectionTitle,
  SegmentTitle,
  SingleSelectionContainer,
  SliderContainer,
  Slider,
  SliderTrack,
  SliderThumb,
  SliderInfos,
  Text,
  TrashIcon,
  SetUpContainer,
  RadioContainer,
  Radio,
  ImageComponent,
  ErrorMessage,
  Checkbox,
  ContainerSavedAudience,
  ActionContainer,
  ActionLink,
} from './styles';
import testTypes from '../../../core/constants/testTypes';
import { deviceTypes, defaultOperatingSystems } from './constants';
import TemplateDropdown from '../template-dropdown';
import { getCatSubCat } from '../../../core/utils/get-cat-subcat';

const Thumb = (props) => <SliderThumb {...props} />;
const Track = (props, state) => <SliderTrack {...props} index={state.index} />;

const Audience = (props) => {
  const [activeIndex, setActiveIndex] = useState();
  const [valuesCatJobs, setValuesCatJobs] = useState([]);
  const [error, setError] = useState(null);
  const [doubleValues, setDoubleValues] = useState([]);

  const {
    values,
    setValues,
    setFieldValue,
    noMarginTop,
    errors,
    audienceTemplateName,
    handleRemoveTemplate,
    handleOpenAudienceModal,
    participantsSource,
    formPrefix,
    useAudienceTemplateEnabled,
    testType,
    projectType,
  } = props;

  const {
    device,
    operatingSystem,
    questions,
    language,
  } = values;

  // if formPrefix is an empty string then this component handles template creation,
  // and does not need to send question id to API.
  const isTemplateCreation = formPrefix === '';

  const initialOS = useMemo(() => {
    const newOperatingSystems = _.map(defaultOperatingSystems, (os) => ({
      ...os,
      checked: _.includes(operatingSystem, os.key),
    }));

    return newOperatingSystems;
  }, [operatingSystem]);

  const [operatingSystems, setOperatingSystems] = useState(initialOS);

  useEffect(() => {
    setOperatingSystems(initialOS);
  }, [initialOS, operatingSystem]);

  const handleSelectedCountries = (arr1, arr2) => _.differenceWith(arr1, arr2, _.isEqual);

  useEffect(() => {
    const getJobsList = async () => {
      try {
        const response = await api.listJobCategories();
        const responseValuesCatJobs = getCatSubCat(response);

        setValuesCatJobs(responseValuesCatJobs);
      } catch (err) {
        setError(err);
      }
    };
    getJobsList();
  }, []);

  useEffect(() => {
    const items = _.pickBy(values.age, (item) => item === true || item.id);

    let ageRange = [];
    Object.entries(items).forEach(([key]) => {
      if (key !== 'Any') {
        ageRange = key.replace('+', '').split('-');
      }
    });
    const ageRangeNum = ageRange.map(Number);

    if (ageRangeNum.length === 0) {
      setDoubleValues([18, 55]);
    } else if (!items.Any) {
      setDoubleValues(ageRangeNum);
    }
  }, [values.age]);

  const selectedItems = useCallback((field) => {
    const items = _.pickBy(values[field], (item) => item === true || item.id);

    if (_.isEmpty(_.pickBy(values[field], (item) => item.id))
      && (field === 'jobsSelected')) return 'Any';

    if (values[field] && values[field].Any) return 'Any';

    if (!_.isEmpty(items)) return 'Selected';
    return null;
  }, [values]);

  const handleSortEndOptions = useCallback(({ oldIndex, newIndex }) => {
    const clone = _.cloneDeep(questions);

    if (clone[oldIndex]) {
      const updatedQuestions = arrayMove(clone, oldIndex, newIndex);

      setValues({
        ...values,
        questions: updatedQuestions,
      });
    }
  }, [questions, setValues, values]);

  const handleNewQuestion = useCallback((
    type,
    question,
    answers,
  ) => {
    let maxValue = 0;

    if (!_.isEmpty(questions)) {
      maxValue = _.max(questions.map((t) => t.index));
    }

    const updatedQuestions = [...questions];
    updatedQuestions.push({
      id: isTemplateCreation ? undefined : v4(),
      type,
      question,
      answers,
      index: maxValue + 1,
      randomize: false,
    });

    setValues({ ...values, questions: updatedQuestions });
  }, [isTemplateCreation, questions, setValues, values]);

  const handleRemoveQuestion = useCallback((questionIndex) => {
    const tempQuestions = [...questions];
    const updatedQuestions = _.pull(tempQuestions, tempQuestions[questionIndex]);

    setValues({ ...values, questions: updatedQuestions });
  }, [setValues, values, questions]);

  const callbackCatAndSubCat = useCallback((items, type) => {
    if (type === 'jobs') {
      setFieldValue('jobsSelected', items);
    }
  }, [setFieldValue]);

  const handleClick = useCallback((e, titleProps) => {
    const { index: titlePropsIndex } = titleProps;
    const newIndex = activeIndex === titlePropsIndex ? -1 : titlePropsIndex;
    setActiveIndex(newIndex);
  }, [activeIndex]);

  const handleChange = useCallback((event, obj, field) => {
    const { name, checked } = obj;
    let futureValues = _.cloneDeep(values[field]);
    const usChanged = field === 'locationCountry' && name === 'us';
    // Update Formik values
    // if Any is checked, check all
    if (name === 'Any') {
      const fields = values[field];
      _.map(fields, (item, key) => {
        fields[key] = checked;
      });
      setValues({
        ...values,
        [field]: fields,
      });
    } else {
      futureValues = {
        ...values[field],
        [name]: checked,
      };
      setValues({
        ...values,
        [field]: futureValues,
      });
    }

    if (futureValues.Any === true
      && !_.isEmpty(_.pickBy(futureValues, (item, key) => item === false && key !== 'Any'))) {
      setValues({
        ...values,
        [field]: {
          ...futureValues,
          Any: false,
        },
      });
    }
    if (futureValues.Any === false
      && _.isEmpty(_.pickBy(futureValues, (item, key) => item === false && key !== 'Any'))) {
      setValues({
        ...values,
        [field]: {
          ...futureValues,
          Any: true,
        },
      });
    }

    // Edge-case: Hide US States and reset values when US is not selected
    if (usChanged && !checked) {
      const locationStateClone = _.clone(values.locationState);
      Object.keys(locationStateClone).forEach((key) => { locationStateClone[key] = true; });

      setValues({
        ...values,
        [field]: { ...futureValues, Any: checked },
        locationState: locationStateClone,
      });
    }
  }, [setValues, values]);

  const handleChangeRadio = useCallback((obj) => {
    const { name } = obj;

    const updatedLanguage = _.mapValues(language, () => false);

    setValues({
      ...values,
      language: {
        ...updatedLanguage,
        [name]: true,
      },
    });
  }, [language, setValues, values]);

  const handleAddAnswer = (arrayHelper, arr) => {
    const newAnswer = { answer: '', type: 'Qualify', optionType: 'answer' };
    const lastOption = _.last(arr);

    // insert before if last item is Other type
    if (lastOption.optionType === 'other') return arrayHelper.insert(arr.length - 1, newAnswer);

    return arrayHelper.push(newAnswer);
  };

  const handleAddOtherAnswer = (arrayHelper) => {
    const otherAnswer = { answer: 'Other', type: 'Qualify', optionType: 'other' };
    return arrayHelper.push(otherAnswer);
  };

  const toggleRandomize = useCallback((question, questionIndex) => {
    const fieldPath = `questions[${questionIndex}].randomize`;
    setFieldValue(fieldPath, !question.randomize);
  }, [setFieldValue]);

  const handleDuplicateQuestion = useCallback((questionIndex) => {
    const duplicatedQuestion = _.cloneDeep(questions[questionIndex]);
    duplicatedQuestion.answers.forEach((answer) => {
      answer.id = isTemplateCreation ? undefined : v4();
    });

    const updatedQuestions = [...questions];
    updatedQuestions.push({
      id: isTemplateCreation ? undefined : v4(),
      type: duplicatedQuestion.type,
      question: duplicatedQuestion.question,
      answers: duplicatedQuestion.answers,
      index: questions.length,
      randomize: duplicatedQuestion.randomize,
    });

    setValues({ ...values, questions: updatedQuestions });
  }, [isTemplateCreation, questions, setValues, values]);

  const handleQuestions = useMemo(() => (
    questions.map((question, indexQuestion) => {
      const questionType = _.capitalize(_.startCase(questions[indexQuestion].type));
      return (
        <QuestionSegment>
          <HeaderSegmentContainer>
            <SegmentTitle>{`Question ${indexQuestion + 1}`}</SegmentTitle>
            <ActionContainer>
              <ActionLink
                onClick={() => handleDuplicateQuestion(indexQuestion)}
              >
                Duplicate
              </ActionLink>
              <TrashIcon
                onClick={() => handleRemoveQuestion(indexQuestion)}
                src="/images/trash-can.png"
                alt="Remove"
              />
            </ActionContainer>
          </HeaderSegmentContainer>

          <QuestionField>
            <Text>
              {`${questionType} question`}
            </Text>
            <Field
              placeholder="Type your screener question here"
              name={`${formPrefix}questions.${indexQuestion}.question`}
              component={TextAreaFormField}
              audience
              minWidth="100%"
            />
          </QuestionField>

          <FieldArray
            name={`${formPrefix}questions.${indexQuestion}.answers`}
            render={(arrayHelpers) => (
              <>
                {questions[indexQuestion].answers.sort((a, b) => a.order - b.order).map((answer, indexAnswer) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <AnswersContainer key={indexAnswer}>
                    <InputAnswerContainer>
                      <Field
                        name={`${formPrefix}questions.${indexQuestion}.answers.${indexAnswer}.answer`}
                        placeholder="Type your answer here"
                        readOnly={
                          questions[indexQuestion].answers[indexAnswer].optionType === 'other'
                        }
                        component={InputFormField}
                        minWidth="325px"
                        audience
                      />
                    </InputAnswerContainer>
                    <QualifyContainer>
                      <Field
                        name={`${formPrefix}questions.${indexQuestion}.answers.${indexAnswer}.type`}
                        isMulti={false}
                        isCreatable={false}
                        options={questions[indexQuestion].type === 'single_select'
                          ? screenerSingle : screenerMultiple}
                        component={SelectFormField}
                        audience
                      />
                    </QualifyContainer>

                    <IconCircle
                      onClick={() => arrayHelpers.remove(indexAnswer)}
                      src="/images/remove.png"
                      alt="Remove"
                    />
                  </AnswersContainer>
                ))}

                <ContainerToggle marginVertical>
                  <Toggle
                    onChange={() => toggleRandomize(questions[indexQuestion], indexQuestion)}
                    checked={questions[indexQuestion].randomize}
                    id={`multiple-select-id-${indexQuestion}`}
                    name="randomize"
                  />
                  <TextToggle fontSize={12}>Randomize answer choices</TextToggle>
                </ContainerToggle>

                <ScreenerQuestionContainer>
                  <AddScreenerQuestion
                    onClick={() => handleAddAnswer(arrayHelpers, questions[indexQuestion].answers)}
                  >
                    + Answer
                  </AddScreenerQuestion>
                </ScreenerQuestionContainer>

                {_.isEmpty(_.pickBy(
                  questions[indexQuestion].answers,
                  (answer) => answer.optionType === 'other',
                ))

                  && (
                    <div>
                      <AddScreenerQuestion
                        onClick={() => handleAddOtherAnswer(arrayHelpers)}
                      >
                        + &#39;Other&#39; as an answer choice
                      </AddScreenerQuestion>
                    </div>
                  )}
              </>
            )}
          />
        </QuestionSegment>
      );
    })
  ), [formPrefix, handleRemoveQuestion, questions, toggleRandomize, handleDuplicateQuestion]);

  /* Handle device changes */
  const handleDeviceChange = (field, value) => {
    if (value === 'computer') {
      setFieldValue('operatingSystem', ['macos', 'windows']);
    } else {
      const newOperatingSystems = defaultOperatingSystems;
      setOperatingSystems(newOperatingSystems);
      setFieldValue('operatingSystem', _.map(newOperatingSystems, 'key'));
    }
    setFieldValue(field, value);
  };

  /* Handle operating system changes */
  const handleOSChange = (key) => {
    const newOperatingSystems = _.map(operatingSystems, (os) => ({
      ...os,
      checked: os.key === key ? !os.checked : os.checked,
    }));

    const newOSFieldValue = _.filter(newOperatingSystems, { checked: true });
    setOperatingSystems(newOperatingSystems);
    setFieldValue('operatingSystem', _.map(newOSFieldValue, 'key'));
  };

  if (error) {
    return (
      <ErrorHandler
        error={error}
        defaultMessage="Error something went wrong!"
      />
    );
  }

  const hasLanguageFilter = testType !== 'card_sort' && testType !== 'tree_test';

  const typeSingle = 'single_select';
  const typeMultiple = 'multiple_select';
  const question = '';

  const answersSingleSelect = [
    {
      id: isTemplateCreation ? undefined : v4(), answer: '', type: 'Qualify', other: false,
    },
    {
      id: isTemplateCreation ? undefined : v4(), answer: '', type: 'Qualify', other: false,
    },
  ];

  const answersMultipleSelect = [
    {
      id: isTemplateCreation ? undefined : v4(), answer: '', type: 'May Select', other: false,
    },
    {
      id: isTemplateCreation ? undefined : v4(), answer: '', type: 'May Select', other: false,
    },
  ];

  return (
    <div>
      <FormContainer>
        <FormikForm>
          <Form as="div">
            {projectType !== testTypes.CARD_SORT && testTypes.TREE_TEST !== projectType && testTypes.SURVEY !== projectType && (
              <>
                <SetUpContainer marginTop>
                  <Text.Bold fontSize="14px">
                    What device should participants use?
                  </Text.Bold>
                  {deviceTypes.map(({ key, image, label }) => (
                    <RadioContainer
                      key={key}
                      onClick={() => handleDeviceChange('device', key)}
                    >
                      <Radio
                        name="device"
                        value={key}
                        checked={device === key}
                      />
                      <ImageComponent src={image} />
                      <Text fontSize="14px">
                        {label}
                      </Text>
                    </RadioContainer>
                  ))}
                </SetUpContainer>
                {errors && errors.device && (
                  <ErrorMessage>{errors.device}</ErrorMessage>
                )}

                {(device === 'smartphone' || device === 'tablet') && (
                  <>
                    <SetUpContainer marginTop>
                      <Text.Bold fontSize="14px">
                        What operating system should participants use?
                      </Text.Bold>
                      {operatingSystems.map((system) => (
                        <RadioContainer
                          key={system.key}
                          onClick={() => handleOSChange(system.key)}
                        >
                          <Checkbox
                            name={`operatingSystem[${system.key}]`}
                            checked={system.checked}
                          />
                          <ImageComponent src={system.image} />
                          <Text fontSize="14px">
                            {system.label}
                          </Text>
                        </RadioContainer>
                      ))}
                    </SetUpContainer>
                    {errors && errors.operatingSystem && (
                      <ErrorMessage>{errors.operatingSystem}</ErrorMessage>
                    )}
                  </>
                )}
              </>
            )}

            {useAudienceTemplateEnabled && (
              <ContainerSavedAudience>
                <TemplateDropdown
                  templateName={audienceTemplateName}
                  onClickRemove={handleRemoveTemplate}
                  onClick={handleOpenAudienceModal}
                  notShowModalToRemove
                  noIcon
                  defaultTitle="Use a saved audience"
                  title="Remove audience"
                  confirmText="Yes, clear audience"
                  cancelText="Cancel, don’t clear audience"
                  chooseText="Choose another audience"
                  removeText="Remove audience"
                />
              </ContainerSavedAudience>
            )}

            <Container noMarginTop={!!noMarginTop}>
              {participantsSource === 'company' && hasLanguageFilter && (
                <SectionContainer>
                  <HeaderQuestions>
                    Localization & Transcription
                  </HeaderQuestions>

                  <Text>
                    Localize the tester platform by selecting a language.
                  </Text>
                  <ContainerAccordion>
                    <CustomAccordion styled>
                      <Accordion.Title
                        active={activeIndex === 10}
                        index={10}
                        onClick={handleClick}
                      >
                        <Icon name="dropdown" />
                        Language (English by default)
                        <FloatRight>{selectedItems('language')}</FloatRight>
                      </Accordion.Title>
                      <Accordion.Content active={activeIndex === 10}>
                        <SelectList
                          singleSelect
                          items={languages}
                          values={language}
                          onChange={(e, obj) => handleChangeRadio(obj)}
                        />
                      </Accordion.Content>
                    </CustomAccordion>
                  </ContainerAccordion>
                </SectionContainer>
              )}

              {participantsSource === 'playbook' && (
                <>
                  <SectionTitle>
                    Demographics
                  </SectionTitle>

                  <SectionContainer>
                    <ContainerAccordion>
                      <CustomAccordion styled>
                        <Accordion.Title
                          active={activeIndex === 0}
                          index={0}
                          onClick={handleClick}
                        >
                          <Icon name="dropdown" />
                          Age
                          <FloatRight>{selectedItems('age')}</FloatRight>
                        </Accordion.Title>
                        <Accordion.Content active={activeIndex === 0}>
                          {activeIndex === 0 && (
                            <SliderContainer>
                              <Slider
                                className="horizontal-slider"
                                min={18}
                                max={55}
                                defaultValue={doubleValues}
                                renderThumb={Thumb}
                                renderTrack={Track}
                                pearling
                                minDistance={5}
                                onChange={(value) => {
                                  setDoubleValues(value);
                                }}
                                onAfterChange={(value) => {
                                  const isAny = !!(value[0] === 18 && value[1] === 55);
                                  setValues({
                                    ...values,
                                    age: {
                                      [`${value[0]}-${value[1] === 55 ? `${value[1]}+` : value[1]}`]: true,
                                      Any: isAny,
                                    },
                                  });
                                }}
                              />
                              <SliderInfos>
                                <Text>{doubleValues[0]}</Text>
                                <Text>{doubleValues[1] === 55 ? `${doubleValues[1]}+` : doubleValues[1]}</Text>
                              </SliderInfos>
                            </SliderContainer>
                          )}

                        </Accordion.Content>
                      </CustomAccordion>
                    </ContainerAccordion>

                    <CheckboxAccordion
                      active={activeIndex === 1}
                      index={1}
                      onClick={handleClick}
                      values={gender}
                      checked={values.gender}
                      onChange={(e, obj) => handleChange(e, obj, 'gender')}
                      selectedItems={selectedItems('gender')}
                      label="Gender"
                    />
                    <CheckboxAccordion
                      active={activeIndex === 2}
                      index={2}
                      onClick={handleClick}
                      values={ethnicity}
                      checked={values.ethnicity}
                      onChange={(e, obj) => handleChange(e, obj, 'ethnicity')}
                      selectedItems={selectedItems('ethnicity')}
                      label="Ethnicity"
                    />

                    <CheckboxAccordion
                      active={activeIndex === 3}
                      index={3}
                      onClick={handleClick}
                      values={householdIncome}
                      checked={values.householdIncome}
                      onChange={(e, obj) => handleChange(e, obj, 'householdIncome')}
                      selectedItems={selectedItems('householdIncome')}
                      label="Household Income"
                    />

                    <ContainerAccordion>
                      <CustomAccordion styled>
                        <Accordion.Title
                          active={activeIndex === 12}
                          index={12}
                          onClick={handleClick}
                        >
                          <Icon name="dropdown" />
                          Location - Country
                          <FloatRight>{selectedItems('locationCountry')}</FloatRight>
                        </Accordion.Title>
                        <Accordion.Content active={activeIndex === 12}>
                          <SelectList
                            items={handleSelectedCountries(countries, remainingCountriesList)}
                            moreItems={remainingCountriesList}
                            values={values.locationCountry}
                            onChange={(e, obj) => handleChange(e, obj, 'locationCountry')}
                          />
                        </Accordion.Content>
                      </CustomAccordion>
                    </ContainerAccordion>

                    {values.locationCountry.us && (
                      <ContainerAccordion>
                        <CustomAccordion styled>
                          <Accordion.Title
                            active={activeIndex === 13}
                            index={13}
                            onClick={handleClick}
                          >
                            <Icon name="dropdown" />
                            Location - US States
                            <FloatRight>{selectedItems('locationState')}</FloatRight>
                          </Accordion.Title>
                          <Accordion.Content active={activeIndex === 13}>
                            <SelectList
                              items={states}
                              values={values.locationState}
                              onChange={(e, obj) => handleChange(e, obj, 'locationState')}
                            />
                          </Accordion.Content>
                        </CustomAccordion>
                      </ContainerAccordion>
                    )}

                    {hasLanguageFilter && (
                      <ContainerAccordion>
                        <CustomAccordion styled>
                          <Accordion.Title
                            active={activeIndex === 10}
                            index={10}
                            onClick={handleClick}
                          >
                            <Icon name="dropdown" />
                            Language (English by default)
                            <FloatRight>{selectedItems('language')}</FloatRight>
                          </Accordion.Title>
                          <Accordion.Content active={activeIndex === 10}>
                            <SelectList
                              singleSelect
                              items={languages}
                              values={values.language}
                              onChange={(e, obj) => handleChangeRadio(obj)}
                            />
                          </Accordion.Content>
                        </CustomAccordion>
                      </ContainerAccordion>
                    )}
                  </SectionContainer>

                  <SectionTitle>
                    Employment
                  </SectionTitle>

                  <SectionContainer>
                    <ContainerAccordion>
                      <CustomAccordion styled>
                        <Accordion.Title
                          active={activeIndex === 5}
                          index={5}
                          onClick={handleClick}
                        >
                          <Icon name="dropdown" />
                          Job Role
                          <FloatRight>{selectedItems('jobsSelected')}</FloatRight>
                        </Accordion.Title>
                        <Accordion.Content active={activeIndex === 5}>
                          <Categorizing
                            category={valuesCatJobs}
                            callbackCatAndSubCat={callbackCatAndSubCat}
                            type="jobs"
                            jobsSelected={values.jobsSelected}
                            values={values.jobsSelected}
                            showEasySelect
                          />
                        </Accordion.Content>
                      </CustomAccordion>
                    </ContainerAccordion>

                    <CheckboxAccordion
                      active={activeIndex === 6}
                      index={6}
                      onClick={handleClick}
                      values={workStatus}
                      checked={values.workStatus}
                      onChange={(e, obj) => handleChange(e, obj, 'workStatus')}
                      selectedItems={selectedItems('workStatus')}
                      label="Current Work Status"
                    />

                    <CheckboxAccordion
                      active={activeIndex === 7}
                      index={7}
                      onClick={handleClick}
                      values={companySize}
                      checked={values.companySize}
                      onChange={(e, obj) => handleChange(e, obj, 'companySize')}
                      selectedItems={selectedItems('companySize')}
                      label="Company Size"
                    />
                    <ContainerAccordion>
                      <CustomAccordion styled>
                        <Accordion.Title
                          active={activeIndex === 8}
                          index={8}
                          onClick={handleClick}
                        >
                          <Icon name="dropdown" />
                          Industry
                          <FloatRight>{selectedItems('industry')}</FloatRight>
                        </Accordion.Title>
                        <Accordion.Content active={activeIndex === 8}>
                          <SelectList
                            items={industry}
                            values={values.industry}
                            onChange={(e, obj) => handleChange(e, obj, 'industry')}
                          />
                        </Accordion.Content>
                      </CustomAccordion>
                    </ContainerAccordion>
                    <CheckboxAccordion
                      active={activeIndex === 9}
                      index={9}
                      onClick={handleClick}
                      values={seniority}
                      checked={values.seniority}
                      onChange={(e, obj) => handleChange(e, obj, 'seniority')}
                      selectedItems={selectedItems('seniority')}
                      label="Seniority"
                    />
                  </SectionContainer>
                </>
              )}
            </Container>
          </Form>
        </FormikForm>
      </FormContainer>

      <SectionContainer>
        <HeaderQuestions>
          Qualifying Questions (Optional)
        </HeaderQuestions>

        <Text>
          These questions will be sent out to qualify participants for your study
        </Text>

        <FieldArray
          name={`${formPrefix}questions`}
          render={() => (
            <Form>
              {questions && (
                <SortableList
                  items={handleQuestions}
                  onSortEnd={handleSortEndOptions}
                  useDragHandle
                />
              )}

              <SingleSelectionContainer>
                <AddQuestion
                  onClick={() => handleNewQuestion(
                    typeSingle,
                    question,
                    answersSingleSelect,
                  )}
                >
                  + Single Select
                </AddQuestion>
              </SingleSelectionContainer>

              <AddQuestion
                onClick={() => handleNewQuestion(
                  typeMultiple,
                  question,
                  answersMultipleSelect,
                )}
              >
                + Multiple Select
              </AddQuestion>
            </Form>
          )}
        />
      </SectionContainer>
    </div>
  );
};

Audience.propTypes = {
  values: PropTypes.shape().isRequired,
  setValues: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  noMarginTop: PropTypes.bool,
  participantsSource: PropTypes.string.isRequired,
  errors: PropTypes.shape({
    device: PropTypes.string,
    operatingSystem: PropTypes.string,
  }),
  audienceTemplateName: PropTypes.string,
  handleRemoveTemplate: PropTypes.func,
  handleOpenAudienceModal: PropTypes.func,
  formPrefix: PropTypes.string,
  useAudienceTemplateEnabled: PropTypes.bool,
  testType: PropTypes.string,
  projectType: PropTypes.string,
};

Audience.defaultProps = {
  noMarginTop: false,
  errors: {},
  formPrefix: '',
  useAudienceTemplateEnabled: false,
  audienceTemplateName: '',
  handleRemoveTemplate: undefined,
  handleOpenAudienceModal: undefined,
  testType: null,
  projectType: null,
};

export default Audience;
